// Decision Tree Questionnaire Configuration
// Defines questions, conditions, and task generation logic

export type QuestionId =
  | "has_passed"
  | "place_of_worship"
  | "worship_type"
  | "funeral_area"
  | "disposition"
  | "has_preplanning"
  | "has_will"
  | "service_type"
  | "reception_planned"
  | "military_service"
  | "has_pets_dependents"
  | "owns_property";

export type AnswerValue = string | boolean;

export interface QuestionOption {
  value: string;
  label: string;
}

export interface Question {
  id: QuestionId;
  text: string;
  subtext?: string;
  type: "boolean" | "single_choice" | "text";
  options?: QuestionOption[];
  showIf?: {
    questionId: QuestionId;
    value: AnswerValue | AnswerValue[];
  };
}

export interface QuestionnaireAnswers {
  [key: string]: AnswerValue;
}

// Questions in order - some are conditional based on previous answers
export const QUESTIONS: Question[] = [
  {
    id: "has_passed",
    text: "Has your loved one already passed away?",
    subtext: "This helps us tailor the checklist to your current situation.",
    type: "boolean",
  },
  {
    id: "has_preplanning",
    text: "Did they have any pre-planned funeral arrangements?",
    subtext: "Some people make arrangements in advance with a funeral home.",
    type: "boolean",
  },
  {
    id: "has_will",
    text: "Do you know if they had a will or advance directives?",
    subtext: "This can include burial wishes, power of attorney, or living will documents.",
    type: "single_choice",
    options: [
      { value: "yes", label: "Yes, there is a will or directives" },
      { value: "no", label: "No, there isn't one" },
      { value: "unknown", label: "I'm not sure" },
    ],
  },
  {
    id: "place_of_worship",
    text: "Did they belong to a place of worship or faith community?",
    subtext: "Religious communities often provide support and may host services.",
    type: "boolean",
  },
  {
    id: "worship_type",
    text: "What type of faith community?",
    subtext: "This helps us suggest appropriate traditions and contacts.",
    type: "single_choice",
    showIf: { questionId: "place_of_worship", value: true },
    options: [
      { value: "christian", label: "Christian (Catholic, Protestant, Orthodox, etc.)" },
      { value: "jewish", label: "Jewish" },
      { value: "muslim", label: "Muslim" },
      { value: "hindu", label: "Hindu" },
      { value: "buddhist", label: "Buddhist" },
      { value: "other", label: "Other faith tradition" },
    ],
  },
  {
    id: "disposition",
    text: "What are the wishes for final disposition?",
    subtext: "This determines many of the practical arrangements needed.",
    type: "single_choice",
    options: [
      { value: "burial", label: "Traditional burial" },
      { value: "cremation", label: "Cremation" },
      { value: "green_burial", label: "Green or natural burial" },
      { value: "donation", label: "Body donation to science" },
      { value: "unknown", label: "Not yet decided" },
    ],
  },
  {
    id: "service_type",
    text: "What type of service are you considering?",
    subtext: "Services can range from traditional funerals to simple gatherings.",
    type: "single_choice",
    options: [
      { value: "traditional", label: "Traditional funeral with visitation" },
      { value: "memorial", label: "Memorial service (no body present)" },
      { value: "graveside", label: "Graveside service only" },
      { value: "celebration", label: "Celebration of life gathering" },
      { value: "private", label: "Private family-only service" },
      { value: "none", label: "No formal service planned" },
    ],
  },
  {
    id: "funeral_area",
    text: "What area will the funeral or memorial be held in?",
    subtext: "This helps us suggest local resources and vendors.",
    type: "text",
  },
  {
    id: "reception_planned",
    text: "Are you planning a reception or gathering after the service?",
    subtext: "Many families host a meal or gathering for friends and family.",
    type: "boolean",
  },
  {
    id: "military_service",
    text: "Did they serve in the military?",
    subtext: "Veterans may be eligible for military honors and benefits.",
    type: "boolean",
  },
  {
    id: "has_pets_dependents",
    text: "Are there pets or dependents that need care arrangements?",
    subtext: "Planning for their care is an important early step.",
    type: "boolean",
  },
  {
    id: "owns_property",
    text: "Did they own property or have significant assets to manage?",
    subtext: "This affects the estate and financial planning tasks.",
    type: "boolean",
  },
];

// Get the next question based on current answers
export function getNextQuestion(answers: QuestionnaireAnswers, currentIndex: number): Question | null {
  for (let i = currentIndex + 1; i < QUESTIONS.length; i++) {
    const question = QUESTIONS[i];

    // Check if this question should be shown based on conditions
    if (question.showIf) {
      const conditionValue = answers[question.showIf.questionId];
      const requiredValues = Array.isArray(question.showIf.value) ? question.showIf.value : [question.showIf.value];

      if (!requiredValues.includes(conditionValue)) {
        continue; // Skip this question
      }
    }

    return question;
  }

  return null; // No more questions
}

// Get the current question index
export function getCurrentQuestionIndex(questionId: QuestionId): number {
  return QUESTIONS.findIndex((q) => q.id === questionId);
}

// Check if questionnaire is complete
export function isQuestionnaireComplete(answers: QuestionnaireAnswers): boolean {
  // Go through all questions and check if applicable ones are answered
  for (const question of QUESTIONS) {
    // Check if question should be shown
    if (question.showIf) {
      const conditionValue = answers[question.showIf.questionId];
      const requiredValues = Array.isArray(question.showIf.value) ? question.showIf.value : [question.showIf.value];

      if (!requiredValues.includes(conditionValue)) {
        continue; // This question was skipped
      }
    }

    // If the question should be shown but isn't answered, not complete
    if (answers[question.id] === undefined) {
      return false;
    }
  }

  return true;
}

// Task categories
export type TaskCategory = "immediate" | "shortTerm" | "longTerm";

export interface ChecklistTask {
  id: string;
  text: string;
  category: TaskCategory;
  // Conditions for when this task should be included
  conditions?: TaskCondition[];
}

export interface TaskCondition {
  questionId: QuestionId;
  operator: "equals" | "not_equals" | "in" | "not_in";
  value: AnswerValue | AnswerValue[];
}

// Master task list with conditions
export const MASTER_TASKS: ChecklistTask[] = [
  // ============================================
  // IMMEDIATE TASKS (first 24-48 hours)
  // ============================================

  // Always shown immediate tasks
  {
    id: "immediate-confirm-death-certificate",
    text: "Confirm that a medical professional will provide a death certificate.",
    category: "immediate",
    conditions: [{ questionId: "has_passed", operator: "equals", value: true }],
  },
  {
    id: "immediate-designate-primary-contact",
    text: "Decide who will be the primary point of contact with the funeral home and family.",
    category: "immediate",
  },
  {
    id: "immediate-notify-close-family",
    text: "Notify close family and friends.",
    category: "immediate",
  },
  {
    id: "immediate-notify-work-or-colleagues",
    text: "Notify the person's workplace or key colleagues, if applicable.",
    category: "immediate",
    conditions: [{ questionId: "has_passed", operator: "equals", value: true }],
  },

  // Pre-planning related
  {
    id: "immediate-contact-preplanned-funeral-home",
    text: "Contact the funeral home where pre-arrangements were made.",
    category: "immediate",
    conditions: [{ questionId: "has_preplanning", operator: "equals", value: true }],
  },
  {
    id: "immediate-contact-funeral-home",
    text: "Research and contact funeral homes or cremation providers in your area.",
    category: "immediate",
    conditions: [{ questionId: "has_preplanning", operator: "equals", value: false }],
  },

  // Will/documents related
  {
    id: "immediate-locate-will",
    text: "Locate and review the will and any advance directives.",
    category: "immediate",
    conditions: [{ questionId: "has_will", operator: "equals", value: "yes" }],
  },
  {
    id: "immediate-search-for-documents",
    text: "Search for any will, advance directives, or important documents.",
    category: "immediate",
    conditions: [{ questionId: "has_will", operator: "in", value: ["no", "unknown"] }],
  },
  {
    id: "immediate-locate-key-documents",
    text: "Gather identification, insurance policies, and financial documents.",
    category: "immediate",
  },

  // Pets/dependents
  {
    id: "immediate-secure-pets-dependents",
    text: "Arrange immediate care for pets and dependents.",
    category: "immediate",
    conditions: [{ questionId: "has_pets_dependents", operator: "equals", value: true }],
  },
  {
    id: "immediate-secure-home",
    text: "Secure the home and valuables.",
    category: "immediate",
  },

  // Religious community
  {
    id: "immediate-notify-place-of-worship",
    text: "Notify their place of worship or faith community.",
    category: "immediate",
    conditions: [{ questionId: "place_of_worship", operator: "equals", value: true }],
  },
  {
    id: "immediate-discuss-religious-customs",
    text: "Discuss religious customs and requirements with clergy or spiritual leader.",
    category: "immediate",
    conditions: [{ questionId: "place_of_worship", operator: "equals", value: true }],
  },

  // Jewish specific - need quick burial traditionally
  {
    id: "immediate-contact-chevra-kadisha",
    text: "Contact the Chevra Kadisha (Jewish burial society) if traditional preparation is desired.",
    category: "immediate",
    conditions: [{ questionId: "worship_type", operator: "equals", value: "jewish" }],
  },

  // Muslim specific - need quick burial traditionally
  {
    id: "immediate-contact-islamic-center",
    text: "Contact the local Islamic center or mosque for guidance on burial traditions.",
    category: "immediate",
    conditions: [{ questionId: "worship_type", operator: "equals", value: "muslim" }],
  },

  // Hindu specific
  {
    id: "immediate-contact-hindu-priest",
    text: "Contact a Hindu priest (pandit) to arrange last rites.",
    category: "immediate",
    conditions: [{ questionId: "worship_type", operator: "equals", value: "hindu" }],
  },

  // Military specific
  {
    id: "immediate-locate-military-records",
    text: "Locate military discharge papers (DD-214) and service records.",
    category: "immediate",
    conditions: [{ questionId: "military_service", operator: "equals", value: true }],
  },

  // Pre-planning
  {
    id: "preplanning-discuss-wishes",
    text: "Discuss and document their wishes for funeral arrangements.",
    category: "immediate",
    conditions: [{ questionId: "has_passed", operator: "equals", value: false }],
  },
  {
    id: "preplanning-research-options",
    text: "Research funeral homes and compare pre-planning options.",
    category: "immediate",
    conditions: [{ questionId: "has_passed", operator: "equals", value: false }],
  },

  // Disposition specific immediate tasks
  {
    id: "immediate-review-organ-donation",
    text: "Review any organ donation wishes and contact the provider if needed.",
    category: "immediate",
    conditions: [
      { questionId: "has_passed", operator: "equals", value: true },
      { questionId: "disposition", operator: "not_equals", value: "donation" },
    ],
  },
  {
    id: "immediate-contact-body-donation-program",
    text: "Contact the body donation program and coordinate transfer.",
    category: "immediate",
    conditions: [{ questionId: "disposition", operator: "equals", value: "donation" }],
  },

  // ============================================
  // SHORT-TERM TASKS (next few days)
  // ============================================

  // Service planning - always shown if having a service
  {
    id: "short-plan-service-details",
    text: "Plan service details: date, time, and format.",
    category: "shortTerm",
    conditions: [{ questionId: "service_type", operator: "not_equals", value: "none" }],
  },
  {
    id: "short-meet-with-funeral-director",
    text: "Meet with the funeral director to review options, costs, and paperwork.",
    category: "shortTerm",
    conditions: [{ questionId: "has_passed", operator: "equals", value: true }],
  },

  // Disposition specific
  {
    id: "short-select-burial-plot",
    text: "Select a burial plot or confirm cemetery arrangements.",
    category: "shortTerm",
    conditions: [{ questionId: "disposition", operator: "in", value: ["burial", "green_burial"] }],
  },
  {
    id: "short-choose-casket",
    text: "Choose a casket or burial container.",
    category: "shortTerm",
    conditions: [{ questionId: "disposition", operator: "in", value: ["burial", "green_burial"] }],
  },
  {
    id: "short-select-urn",
    text: "Select an urn or decide on cremains disposition.",
    category: "shortTerm",
    conditions: [{ questionId: "disposition", operator: "equals", value: "cremation" }],
  },
  {
    id: "short-green-burial-requirements",
    text: "Review green burial requirements and select an approved shroud or container.",
    category: "shortTerm",
    conditions: [{ questionId: "disposition", operator: "equals", value: "green_burial" }],
  },

  // Service type specific
  {
    id: "short-arrange-visitation",
    text: "Arrange visitation hours and viewing details.",
    category: "shortTerm",
    conditions: [{ questionId: "service_type", operator: "equals", value: "traditional" }],
  },
  {
    id: "short-choose-venue-church",
    text: "Coordinate with the place of worship for the service.",
    category: "shortTerm",
    conditions: [
      { questionId: "place_of_worship", operator: "equals", value: true },
      { questionId: "service_type", operator: "not_equals", value: "none" },
    ],
  },
  {
    id: "short-choose-venue-other",
    text: "Choose a venue for the service (funeral home, outdoor space, community center, etc.).",
    category: "shortTerm",
    conditions: [
      { questionId: "place_of_worship", operator: "equals", value: false },
      { questionId: "service_type", operator: "not_in", value: ["none", "graveside"] },
    ],
  },
  {
    id: "short-coordinate-officiant-clergy",
    text: "Coordinate with clergy or spiritual leader to officiate the service.",
    category: "shortTerm",
    conditions: [
      { questionId: "place_of_worship", operator: "equals", value: true },
      { questionId: "service_type", operator: "not_equals", value: "none" },
    ],
  },
  {
    id: "short-find-officiant",
    text: "Find an officiant or celebrant for the service.",
    category: "shortTerm",
    conditions: [
      { questionId: "place_of_worship", operator: "equals", value: false },
      { questionId: "service_type", operator: "not_in", value: ["none", "private"] },
    ],
  },

  // Military honors
  {
    id: "short-request-military-honors",
    text: "Request military funeral honors through the funeral home or VA.",
    category: "shortTerm",
    conditions: [{ questionId: "military_service", operator: "equals", value: true }],
  },
  {
    id: "short-arrange-flag-presentation",
    text: "Arrange for flag folding and presentation ceremony.",
    category: "shortTerm",
    conditions: [{ questionId: "military_service", operator: "equals", value: true }],
  },

  // General service tasks
  {
    id: "short-write-obituary",
    text: "Draft an obituary and decide where it will be shared.",
    category: "shortTerm",
  },
  {
    id: "short-gather-photos",
    text: "Gather photos for the service, slideshow, or tribute page.",
    category: "shortTerm",
  },
  {
    id: "short-select-readings-music",
    text: "Select readings, poems, or music for the service.",
    category: "shortTerm",
    conditions: [{ questionId: "service_type", operator: "not_equals", value: "none" }],
  },
  {
    id: "short-coordinate-pallbearers",
    text: "Ask and confirm who will serve as pallbearers.",
    category: "shortTerm",
    conditions: [{ questionId: "disposition", operator: "equals", value: "burial" }],
  },
  {
    id: "short-plan-livestream",
    text: "Plan livestream or virtual attendance options for those who cannot travel.",
    category: "shortTerm",
    conditions: [{ questionId: "service_type", operator: "not_in", value: ["none", "private"] }],
  },
  {
    id: "short-order-flowers",
    text: "Order flowers or decide on charitable donations in lieu of flowers.",
    category: "shortTerm",
    conditions: [{ questionId: "service_type", operator: "not_equals", value: "none" }],
  },
  {
    id: "short-order-printed-materials",
    text: "Order printed materials (programs, prayer cards, or handouts).",
    category: "shortTerm",
    conditions: [{ questionId: "service_type", operator: "not_in", value: ["none", "private"] }],
  },
  {
    id: "short-gather-clothing",
    text: "Gather clothing or personal items for burial or cremation.",
    category: "shortTerm",
    conditions: [{ questionId: "has_passed", operator: "equals", value: true }],
  },

  // Reception tasks
  {
    id: "short-plan-reception-venue",
    text: "Book a venue for the reception or gathering.",
    category: "shortTerm",
    conditions: [{ questionId: "reception_planned", operator: "equals", value: true }],
  },
  {
    id: "short-arrange-catering",
    text: "Arrange catering or food for the reception.",
    category: "shortTerm",
    conditions: [{ questionId: "reception_planned", operator: "equals", value: true }],
  },
  {
    id: "short-reception-logistics",
    text: "Plan reception logistics: parking, seating, and timing.",
    category: "shortTerm",
    conditions: [{ questionId: "reception_planned", operator: "equals", value: true }],
  },

  // Transportation
  {
    id: "short-arrange-transportation",
    text: "Arrange transportation for close family members.",
    category: "shortTerm",
    conditions: [{ questionId: "service_type", operator: "not_equals", value: "none" }],
  },

  // Pre-planning short term
  {
    id: "preplanning-compare-costs",
    text: "Compare costs and services from different funeral homes.",
    category: "shortTerm",
    conditions: [{ questionId: "has_passed", operator: "equals", value: false }],
  },
  {
    id: "preplanning-document-wishes",
    text: "Create a written document of all funeral preferences.",
    category: "shortTerm",
    conditions: [{ questionId: "has_passed", operator: "equals", value: false }],
  },
  {
    id: "preplanning-consider-prepayment",
    text: "Consider pre-payment options to lock in current prices.",
    category: "shortTerm",
    conditions: [{ questionId: "has_passed", operator: "equals", value: false }],
  },

  // ============================================
  // LONG-TERM TASKS (later)
  // ============================================

  // Death certificates and official notifications
  {
    id: "long-request-death-certificates",
    text: "Request multiple certified copies of the death certificate.",
    category: "longTerm",
    conditions: [{ questionId: "has_passed", operator: "equals", value: true }],
  },
  {
    id: "long-notify-social-security",
    text: "Confirm Social Security has been notified (funeral homes often do this).",
    category: "longTerm",
    conditions: [{ questionId: "has_passed", operator: "equals", value: true }],
  },

  // Financial and accounts
  {
    id: "long-notify-banks",
    text: "Notify banks, lenders, and financial institutions.",
    category: "longTerm",
    conditions: [{ questionId: "has_passed", operator: "equals", value: true }],
  },
  {
    id: "long-close-accounts",
    text: "Close or update accounts and subscriptions.",
    category: "longTerm",
    conditions: [{ questionId: "has_passed", operator: "equals", value: true }],
  },
  {
    id: "long-close-social-media",
    text: "Close or memorialize social media accounts.",
    category: "longTerm",
    conditions: [{ questionId: "has_passed", operator: "equals", value: true }],
  },

  // Estate
  {
    id: "long-settle-estate",
    text: "Begin settling the estate with legal or financial advisors.",
    category: "longTerm",
    conditions: [{ questionId: "has_passed", operator: "equals", value: true }],
  },
  {
    id: "long-probate-process",
    text: "Begin the probate process if required.",
    category: "longTerm",
    conditions: [
      { questionId: "has_passed", operator: "equals", value: true },
      { questionId: "has_will", operator: "equals", value: "yes" },
    ],
  },

  // Property specific
  {
    id: "long-manage-property",
    text: "Make plans for managing or selling property.",
    category: "longTerm",
    conditions: [{ questionId: "owns_property", operator: "equals", value: true }],
  },
  {
    id: "long-property-insurance",
    text: "Review and update property insurance policies.",
    category: "longTerm",
    conditions: [{ questionId: "owns_property", operator: "equals", value: true }],
  },
  {
    id: "long-property-maintenance",
    text: "Arrange for ongoing property maintenance until decisions are made.",
    category: "longTerm",
    conditions: [{ questionId: "owns_property", operator: "equals", value: true }],
  },

  // Pets long-term
  {
    id: "long-permanent-pet-arrangements",
    text: "Make permanent arrangements for pets.",
    category: "longTerm",
    conditions: [{ questionId: "has_pets_dependents", operator: "equals", value: true }],
  },

  // Military benefits
  {
    id: "long-apply-va-benefits",
    text: "Apply for VA survivor benefits and burial allowance.",
    category: "longTerm",
    conditions: [{ questionId: "military_service", operator: "equals", value: true }],
  },
  {
    id: "long-order-headstone-marker",
    text: "Order a headstone or grave marker (VA provides free for veterans).",
    category: "longTerm",
    conditions: [
      { questionId: "military_service", operator: "equals", value: true },
      { questionId: "disposition", operator: "in", value: ["burial", "green_burial"] },
    ],
  },

  // General long-term
  {
    id: "long-update-beneficiaries",
    text: "Review and update your own beneficiaries on accounts and policies.",
    category: "longTerm",
    conditions: [{ questionId: "has_passed", operator: "equals", value: true }],
  },
  {
    id: "long-return-medical-equipment",
    text: "Return or arrange pickup of medical equipment.",
    category: "longTerm",
    conditions: [{ questionId: "has_passed", operator: "equals", value: true }],
  },
  {
    id: "long-thank-you-notes",
    text: "Send thank-you notes to those who provided support.",
    category: "longTerm",
  },
  {
    id: "long-schedule-financial-review",
    text: "Schedule time with a financial planner to review your situation.",
    category: "longTerm",
  },
  {
    id: "long-memorial-plans",
    text: "Consider longer-term memorials (donations, legacy projects, scholarships).",
    category: "longTerm",
  },
  {
    id: "long-create-memory-book",
    text: "Create a memory book or digital album with photos and stories.",
    category: "longTerm",
  },
  {
    id: "long-heirlooms-belongings",
    text: "Make plans for personal belongings and heirlooms.",
    category: "longTerm",
    conditions: [{ questionId: "has_passed", operator: "equals", value: true }],
  },

  // Cremation specific long-term
  {
    id: "long-cremains-final-disposition",
    text: "Decide on final disposition of cremains (scattering, burial, keeping).",
    category: "longTerm",
    conditions: [{ questionId: "disposition", operator: "equals", value: "cremation" }],
  },

  // Pre-planning long-term
  {
    id: "preplanning-inform-family",
    text: "Inform family members where pre-planning documents are kept.",
    category: "longTerm",
    conditions: [{ questionId: "has_passed", operator: "equals", value: false }],
  },
  {
    id: "preplanning-review-annually",
    text: "Set a reminder to review and update arrangements annually.",
    category: "longTerm",
    conditions: [{ questionId: "has_passed", operator: "equals", value: false }],
  },
  {
    id: "preplanning-create-directive",
    text: "Create or update advance directives and healthcare proxy.",
    category: "longTerm",
    conditions: [{ questionId: "has_passed", operator: "equals", value: false }],
  },
];

// Check if a single condition is met
function checkCondition(condition: TaskCondition, answers: QuestionnaireAnswers): boolean {
  const answerValue = answers[condition.questionId];

  // If the question hasn't been answered, condition is not met
  if (answerValue === undefined) {
    return false;
  }

  switch (condition.operator) {
    case "equals":
      return answerValue === condition.value;
    case "not_equals":
      return answerValue !== condition.value;
    case "in":
      return Array.isArray(condition.value) && condition.value.includes(answerValue);
    case "not_in":
      return Array.isArray(condition.value) && !condition.value.includes(answerValue);
    default:
      return false;
  }
}

// Generate personalized task list based on questionnaire answers
export function generateTaskList(answers: QuestionnaireAnswers): ChecklistTask[] {
  return MASTER_TASKS.filter((task) => {
    // If no conditions, always include
    if (!task.conditions || task.conditions.length === 0) {
      return true;
    }

    // All conditions must be met (AND logic)
    return task.conditions.every((condition) => checkCondition(condition, answers));
  });
}

// Storage keys
export const QUESTIONNAIRE_STORAGE_KEY = "starlit-questionnaire-v1";
export const QUESTIONNAIRE_ANSWERS_KEY = "starlit-questionnaire-answers-v1";
export const CHECKLIST_COMPLETED_KEY = "starlit-checklist-completed-v1";
